import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { usePrompts } from '../contexts/PromptContext';
import { Prompt } from '../types';
import AddPromptModal from './AddPromptModal';
import EditPromptModal from './EditPromptModal';
import PromptDetailModal from './PromptDetailModal';
import PromptCard from './PromptCard';
import VersionHistoryModal from './VersionHistoryModal';
import SEOHead from './SEOHead';
import toast from 'react-hot-toast';
import { promptsApi } from '../services/api';

// 已购买的提示词类型扩展
interface PurchasedPrompt extends Prompt {
  isPurchased?: boolean;
  purchasedAt?: string;
}

const Dashboard: React.FC = () => {
  const { prompts, filteredPrompts, searchQuery, isLoading, deletePrompt, togglePublicPrompt, loadPrompts, searchPrompts } = usePrompts();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [versionPrompt, setVersionPrompt] = useState<Prompt | null>(null);
  const [currentUser, setCurrentUser] = useState<{ username: string } | null>(null);
  
  // 新增：已购买的提示词和标签页状态
  const [purchasedPrompts, setPurchasedPrompts] = useState<PurchasedPrompt[]>([]);
  const [isPurchasedLoading, setIsPurchasedLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'created' | 'purchased'>('created');

  const recentPrompts = prompts.slice(0, 3);
  const thisWeekCount = prompts.filter(p => 
    new Date(p.createdAt) > new Date(Date.now() - 7*24*60*60*1000)
  ).length;

  const displayPrompts = searchQuery ? filteredPrompts : prompts;
  
  // 加载已购买的提示词
  const loadPurchasedPrompts = useCallback(async () => {
    try {
      setIsPurchasedLoading(true);
      const result = await promptsApi.getPurchased();
      if (result.success) {
        setPurchasedPrompts(result.data || []);
      }
    } catch (error) {
      console.error('加载已购买提示词失败:', error);
    } finally {
      setIsPurchasedLoading(false);
    }
  }, []);

  // 初始化时加载已购买的提示词
  useEffect(() => {
    loadPurchasedPrompts();
  }, [loadPurchasedPrompts]);

  // 获取当前用户信息
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch('/api/user/profile', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        const data = await response.json();
        if (data.success) {
          setCurrentUser({ username: data.data.username });
        }
      } catch (error) {
        console.error('获取用户信息失败:', error);
      }
    };

    fetchCurrentUser();
  }, []);

  const copyToClipboard = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      toast.success('提示词已复制到剪贴板！');
    } catch (error) {
      console.error('复制失败:', error);
      toast.error('复制失败，请手动选择文本');
    }
  };

  const handleDeletePrompt = async (id: number) => {
    if (deletePrompt) {
      try {
        await deletePrompt(id);
      } catch (error) {
        // 错误处理已在context中完成
      }
    }
  };

  const handleTogglePublic = async (id: number) => {
    if (togglePublicPrompt) {
      try {
        await togglePublicPrompt(id);
      } catch (error) {
        // 错误处理已在context中完成
      }
    }
  };

  const handleEditPrompt = (prompt: Prompt) => {
    setEditingPrompt(prompt);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingPrompt(null);
  };

  const handleEditFromDetail = () => {
    if (selectedPrompt) {
      setEditingPrompt(selectedPrompt);
      setSelectedPrompt(null); // 关闭详情模态框
      setShowEditModal(true); // 打开编辑模态框
    }
  };

  const handleViewVersions = (prompt: Prompt) => {
    setVersionPrompt(prompt);
    setShowVersionModal(true);
  };

  const handleCloseVersionModal = () => {
    setShowVersionModal(false);
    setVersionPrompt(null);
  };

  const handleVersionRestore = async () => {
    // 版本恢复后重新加载提示词列表
    if (loadPrompts) {
      await loadPrompts();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      <SEOHead 
        title="我的提示词 - AI咖：专业的AI提示词管理平台"
        description="管理您的AI提示词，支持ChatGPT、Claude等AI工具的提示词创建、编辑和分类管理，提高创作效率"
      />

      <main className="pt-8" role="main">
        {/* Hero Section with Bento Grid */}
        <section className="py-16 bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 transition-colors duration-200" aria-label="个人化问候">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <div className="inline-block">
                <h1 className="text-6xl lg:text-7xl font-black gradient-text mb-4">
                  你好，{currentUser?.username || '用户'}
                </h1>
                <p className="text-xl text-gray-600 dark:text-gray-300 font-medium">
                  这里管理的不仅是提示词，更是你的经验与知识
                </p>
              </div>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* 统计卡片 */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-lg border border-gray-100 dark:border-gray-700 relative overflow-hidden transition-colors duration-200"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary bg-opacity-5 rounded-full -translate-y-16 translate-x-16"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-green-500 bg-opacity-5 rounded-full translate-y-12 -translate-x-12"></div>
                
                <div className="relative z-10 h-full flex flex-col">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">创作统计</h3>
                    <i className="fas fa-chart-line text-2xl text-primary"></i>
                  </div>
                  
                  <div className="flex-1 flex flex-col justify-center">
                    <div className="grid grid-cols-2 gap-8 mb-6">
                      <div className="text-center">
                        <div className="text-4xl lg:text-5xl font-black text-primary mb-2">
                          {prompts.length}
                        </div>
                        <div className="text-sm font-medium text-gray-700 dark:text-gray-300">总提示词</div>
                      </div>
                      <div className="text-center">
                        <div className="text-4xl lg:text-5xl font-black text-green-500 mb-2">
                          {thisWeekCount}
                        </div>
                        <div className="text-sm font-medium text-gray-700 dark:text-gray-300">本周新增</div>
                      </div>
                    </div>
                    
                    <div className="border-t dark:border-gray-700 pt-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">创作活跃度</span>
                        <div className="flex items-center">
                          <div className={`w-2 h-2 rounded-full mr-2 ${thisWeekCount > 0 ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                          <span className={`font-semibold ${thisWeekCount > 0 ? 'text-green-600' : 'text-gray-500'}`}>
                            {thisWeekCount > 0 ? '活跃' : '平静'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* 最近提示词 */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-lg border border-gray-100 dark:border-gray-700 transition-colors duration-200"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">最近提示词</h3>
                  <i className="fas fa-clock text-gray-400 dark:text-gray-500"></i>
                </div>
                <div className="space-y-4">
                  {recentPrompts.length > 0 ? (
                    recentPrompts.map((prompt) => (
                      <motion.div
                        key={prompt.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3 }}
                        onClick={() => setSelectedPrompt(prompt)}
                        className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors cursor-pointer"
                      >
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 dark:text-white mb-1 line-clamp-1">
                            {prompt.title}
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-1">
                            {prompt.content.substring(0, 50)}...
                          </p>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(prompt.content);
                          }}
                          title="复制提示词内容"
                          className="ml-4 p-2 text-gray-400 dark:text-gray-500 hover:text-primary transition-colors"
                        >
                          <i className="fas fa-copy"></i>
                        </button>
                      </motion.div>
                    ))
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400 text-center py-8">暂无提示词</p>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* 提示词列表 */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            {/* 搜索和操作栏 */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-4 mb-8 border border-gray-100 dark:border-gray-700">
              <div className="flex flex-col sm:flex-row items-center gap-4">
                {/* 搜索框 */}
                <div className="relative flex-1 w-full">
                  <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => searchPrompts(e.target.value)}
                    placeholder="搜索提示词标题或内容..."
                    className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => searchPrompts('')}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
                
                {/* 添加按钮 */}
                <button
                  onClick={() => setShowAddModal(true)}
                  className="bg-primary hover:bg-primary-dark text-white px-6 py-3 rounded-xl transition-all transform hover:scale-105 shadow-sm whitespace-nowrap flex items-center"
                >
                  <i className="fas fa-plus mr-2"></i>
                  添加提示词
                </button>
              </div>
            </div>

            {/* 标签页切换 */}
            <div className="flex items-center gap-4 mb-6">
              <button
                onClick={() => setActiveTab('created')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  activeTab === 'created'
                    ? 'bg-primary text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600'
                }`}
              >
                <i className="fas fa-pencil-alt mr-2"></i>
                我创建的 ({prompts.length})
              </button>
              <button
                onClick={() => setActiveTab('purchased')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  activeTab === 'purchased'
                    ? 'bg-primary text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600'
                }`}
              >
                <i className="fas fa-shopping-bag mr-2"></i>
                我购买的 ({purchasedPrompts.length})
              </button>
            </div>

            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {activeTab === 'created' 
                  ? (searchQuery ? `搜索结果 (${filteredPrompts.length})` : `我创建的提示词 (${prompts.length})`)
                  : `我购买的提示词 (${purchasedPrompts.length})`
                }
              </h2>
            </div>

            {/* 我创建的提示词 */}
            {activeTab === 'created' && (
              <>
            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : displayPrompts.length === 0 ? (
              <div className="text-center py-16">
                <i className={`fas fa-${searchQuery ? 'search' : 'file-alt'} text-6xl text-gray-300 dark:text-gray-600 mb-4`}></i>
                <h3 className="text-xl font-semibold text-gray-600 dark:text-gray-400 mb-2">
                  {searchQuery ? '未找到匹配的提示词' : '还没有提示词'}
                </h3>
                <p className="text-gray-500 dark:text-gray-500">
                  {searchQuery ? '尝试使用其他关键词搜索' : '点击上方按钮创建您的第一个提示词'}
                </p>
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
                className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
              >
                {displayPrompts.map((prompt, index) => (
                  <PromptCard
                    key={prompt.id}
                    prompt={prompt}
                    index={index}
                    onView={() => setSelectedPrompt(prompt)}
                    onCopy={() => copyToClipboard(prompt.content)}
                    onEdit={() => handleEditPrompt(prompt)}
                    onDelete={handleDeletePrompt}
                    onTogglePublic={handleTogglePublic}
                    onViewVersions={() => handleViewVersions(prompt)}
                  />
                ))}
              </motion.div>
                )}
              </>
            )}

            {/* 我购买的提示词 */}
            {activeTab === 'purchased' && (
              <>
                {isPurchasedLoading ? (
                  <div className="flex justify-center py-12">
                    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : purchasedPrompts.length === 0 ? (
                  <div className="text-center py-16">
                    <i className="fas fa-shopping-bag text-6xl text-gray-300 dark:text-gray-600 mb-4"></i>
                    <h3 className="text-xl font-semibold text-gray-600 dark:text-gray-400 mb-2">
                      还没有购买的提示词
                    </h3>
                    <p className="text-gray-500 dark:text-gray-500">
                      去提示词广场发现并购买精彩的提示词
                    </p>
                  </div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6 }}
                    className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
                  >
                    {purchasedPrompts.map((prompt, index) => (
                      <motion.div
                        key={`purchased-${prompt.id}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow"
                      >
                        {/* 已购买标签 */}
                        <div className="relative">
                          <div className="absolute top-3 left-3 z-10 px-2 py-1 bg-emerald-500 text-white text-xs font-medium rounded-full">
                            <i className="fas fa-check mr-1"></i>
                            已购买
                          </div>
                          {prompt.images && prompt.images.length > 0 ? (
                            <img 
                              src={prompt.images[0]} 
                              alt={prompt.title}
                              className="w-full h-40 object-cover"
                            />
                          ) : (
                            <div className="w-full h-40 bg-gradient-to-br from-primary/10 to-purple-500/10 flex items-center justify-center">
                              <i className="fas fa-file-alt text-4xl text-primary/30"></i>
                            </div>
                          )}
                        </div>
                        
                        <div className="p-5">
                          <div className="flex items-start justify-between mb-2">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white line-clamp-1">
                              {prompt.title}
                            </h3>
                            <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full">
                              {prompt.category}
                            </span>
                          </div>
                          
                          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-3">
                            {prompt.content.substring(0, 80)}...
                          </p>
                          
                          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-4">
                            <span>
                              <i className="fas fa-user mr-1"></i>
                              {prompt.author || '未知作者'}
                            </span>
                            <span>
                              购买于 {new Date(prompt.purchasedAt || prompt.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setSelectedPrompt(prompt)}
                              className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm"
                            >
                              <i className="fas fa-eye mr-1.5"></i>
                              查看详情
                            </button>
                            <button
                              onClick={() => copyToClipboard(prompt.content)}
                              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
                            >
                              <i className="fas fa-copy"></i>
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </>
            )}
          </div>
        </section>
      </main>

      {/* 模态框 */}
      <AddPromptModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
      />

      <EditPromptModal
        prompt={editingPrompt}
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
      />

      <PromptDetailModal
        prompt={selectedPrompt}
        isOpen={!!selectedPrompt}
        onClose={() => setSelectedPrompt(null)}
        onCopy={(content: string) => copyToClipboard(content)}
        onEdit={handleEditFromDetail}
        onDelete={handleDeletePrompt}
        allPrompts={prompts}
        onPromptSelect={(newPrompt) => setSelectedPrompt(newPrompt)}
      />

      <VersionHistoryModal
        isOpen={showVersionModal}
        onClose={handleCloseVersionModal}
        promptId={versionPrompt?.id || 0}
        promptTitle={versionPrompt?.title || ''}
        onVersionRestore={handleVersionRestore}
      />
    </div>
  );
};

export default Dashboard; 